import {
  ACTIVE_COUPONS_DEFAULT_LIMIT,
  ACTIVE_COUPONS_DEFAULT_OFFSET,
} from "constants/coupons";
import { mapActiveCouponFromApi } from "helpers/couponApi";
import {
  getApiErrorMessage,
  isPackageManagementApiSuccess,
  parseFetchResponseBody,
} from "helpers/packageManagementApi";

const PACKAGE_MANAGEMENT_URL =
  process.env.REACT_APP_PACKAGE_MANAGEMENT_URL?.trim() ?? "";
const PACKAGE_MANAGEMENT_SECRET_KEY =
  process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY ?? "";

const buildUrl = (path, searchParams = {}) => {
  const baseUrl = PACKAGE_MANAGEMENT_URL.replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  const url = new URL(normalizedPath, `${baseUrl}/`);

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });

  return url;
};

const getHeaders = (additionalHeaders = {}) => ({
  accept: "application/json",
  "secret-key": PACKAGE_MANAGEMENT_SECRET_KEY,
  ...additionalHeaders,
});

const request = async (path, { method = "GET", searchParams, headers, body } = {}) => {
  const url = buildUrl(path, searchParams);
  const fetchOptions = { method, headers: getHeaders(headers) };

  if (body !== undefined) {
    fetchOptions.body = body;
  }

  const response = await fetch(url, fetchOptions);
  const data = await response.json().catch(() => ({}));

  return { response, data };
};

const requestWithParsedBody = async (
  path,
  { method = "GET", searchParams, headers, body, fallbackMessage }
) => {
  const url = buildUrl(path, searchParams);
  const fetchOptions = { method, headers: getHeaders(headers) };

  if (body !== undefined) {
    fetchOptions.body = body;
  }

  const response = await fetch(url, fetchOptions);
  const data = await parseFetchResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getApiErrorMessage(data, fallbackMessage, response.status)
    );
  }

  return { response, data };
};

const assertLegacySuccess = (response, data) => {
  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  if (data.status && data.status === "success") {
    return data.data;
  }

  throw new Error("Unexpected API response format");
};

export const searchPackages = async (searchTerm) => {
  const { response, data } = await request("/search-packages", {
    searchParams: { name: searchTerm },
  });

  return assertLegacySuccess(response, data);
};

export const fetchPackageDetails = async (packageGroupId) => {
  const { response, data } = await request(`/get-package/${packageGroupId}`);
  return assertLegacySuccess(response, data);
};

export const fetchPackageVersion = async (packageId) => {
  const { response, data } = await request(`/get-package-version/${packageId}`);

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  if (data.status === "success") {
    return data.data;
  }

  throw new Error("Unexpected API response format");
};

export const createPackageVersion = async ({ packageGroupId, payload }) => {
  const { response, data } = await request(
    `/create-package-version/${packageGroupId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(data.message || `Error: ${response.statusText}`);
  }

  if (data.status && data.status === "success") {
    return data.data;
  }

  throw new Error("Unexpected API response format");
};

export const fetchServiceUsageDates = async () => {
  const { response, data } = await request("/get-service-selling-dates");
  const result = assertLegacySuccess(response, data);

  return result.map((date) => ({
    id: date.id,
    label: `${date.start_usage_date} to ${date.end_usage_date}`,
    start_usage_date: date.start_usage_date,
    end_usage_date: date.end_usage_date,
    value: date.id,
  }));
};

export const editPackage = async ({ packageId, payload }) => {
  const { response, data } = await request(`/edit-package/${packageId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(data.message || `Error: ${response.statusText}`);
  }

  if (data.status === "success") {
    return data.data;
  }

  throw new Error(
    data.message || "An error occurred while processing the request."
  );
};

export const createCoupon = async (payload) => {
  const { data } = await requestWithParsedBody("/create-coupon", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    fallbackMessage: "Failed to create coupon.",
  });

  if (data.status && data.status === "success") {
    return data.data;
  }

  throw new Error(getApiErrorMessage(data, "Failed to create coupon."));
};

export const fetchActiveCoupons = async ({
  offset = ACTIVE_COUPONS_DEFAULT_OFFSET,
  limit = ACTIVE_COUPONS_DEFAULT_LIMIT,
} = {}) => {
  const { data } = await requestWithParsedBody("/active-coupons", {
    searchParams: { offset, limit },
    fallbackMessage: "Failed to fetch active coupons.",
  });

  const payload = data.data ?? data;
  const coupons = (payload.coupons ?? []).map(mapActiveCouponFromApi);

  return {
    offset: payload.offset ?? offset,
    limit: payload.limit ?? limit,
    coupons,
  };
};

export const fetchCouponInfo = async (couponCode) => {
  const trimmedCode = couponCode?.trim();
  if (!trimmedCode) {
    throw new Error("Coupon code is required.");
  }

  const { data, response } = await requestWithParsedBody(
    `/get-coupon-info/${encodeURIComponent(trimmedCode)}`,
    { fallbackMessage: "Failed to fetch coupon info." }
  );

  if (!isPackageManagementApiSuccess(data.status)) {
    throw new Error(
      getApiErrorMessage(data, "Failed to fetch coupon info.", response.status)
    );
  }

  return data.data ?? data;
};

export const editCoupon = async ({ couponCode, payload }) => {
  const trimmedCode = couponCode?.trim();
  if (!trimmedCode) {
    throw new Error("Coupon code is required.");
  }

  const { data, response } = await requestWithParsedBody(
    `/edit-coupon/${encodeURIComponent(trimmedCode)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      fallbackMessage: "Failed to update coupon.",
    }
  );

  if (isPackageManagementApiSuccess(data.status)) {
    return data.data ?? data;
  }

  throw new Error(
    getApiErrorMessage(data, "Failed to update coupon.", response.status)
  );
};
